import React, { useState, useEffect } from "react";
import {
  ChatWrapper,
  MessageBubble,
  ChatInputWrapper,
  Input,
  MessageContainer,
  Avatar,
  MessageContent,
  MessageActions,
} from "./chatInterface.styled";
import SendIcon from "@mui/icons-material/Send";
import IconButton from "@mui/material/IconButton";
import PersonIcon from "@mui/icons-material/Person";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import RefreshIcon from "@mui/icons-material/Refresh";
import RadioButtonCheckedIcon from "@mui/icons-material/RadioButtonChecked";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import { fetchBaseUrl } from "core/utils/common";
import CustomMarkdown from "core/components/global/markdown/markdown";
import { v4 as uuidv4 } from 'uuid';
import { styled } from "styled-components";

interface Message {
  content: string;
  isUser: boolean;
  timestamp: Date;
}

interface Task {
  name: string;
  description: string;
  capabilities: string[];
  type: string;
}

interface StandaloneChatInterfaceProps {
  theme: any;
  alertId?: string;
}

const TaskSelector = styled.div`
  border-radius: 8px;
`;

const TaskSelectorTitle = styled.h3`
  font-size: 16px;
  font-weight: 500;
  color: white;
`;

const TaskOptions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

interface TaskOptionProps {
  theme: any;
  selected?: boolean;
}

const TaskOption = styled.div<TaskOptionProps>`
  display: flex;
  align-items: center;
  padding: 8px 16px;
  border-radius: 8px;
  background-color: ${({ selected }) => selected ? '#9333ea' : '#333333'};
  color: white;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 14px;
  font-weight: 500;
  margin: 4px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  
  &:hover {
    background-color: ${({ selected }) => selected ? '#a855f7' : '#444444'};
  }
`;

const baseURL = fetchBaseUrl({port: 8001});

const StandaloneChatInterface: React.FC<StandaloneChatInterfaceProps> = ({
  theme,
  alertId
}) => {
  const [userPrompt, setUserPrompt] = useState<string>("");
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [conversationId, setConversationId] = useState<string>("");
  const [availableTasks, setAvailableTasks] = useState<Task[]>([]);
  const [selectedTask, setSelectedTask] = useState<string>("");
  const [tasksLoading, setTasksLoading] = useState(false);

  // Generate a conversation ID and fetch available tasks when the component mounts
  useEffect(() => {
    setConversationId(uuidv4());
    fetchAvailableTasks();
  }, []);

  // Fetch available tasks from the API
  const fetchAvailableTasks = async () => {
    setTasksLoading(true);
    
    try {
      const response = await fetch(`${baseURL}/ac2/api/tasks`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "X-API-Key": "dev-key",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      // Get all available tasks
      const allTasks = data.tasks || [];
      console.log("Available tasks:", allTasks);
      
      // Create default tasks if none are returned from API
      let filteredTasks = allTasks.filter(task => 
        task.name === "knowledge_base_search" || task.name === "datalake_chat"
      );
      
      // Add display names to the tasks
      const tasksWithdescriptions = filteredTasks.map(task => ({
        ...task,
        description: task.name === "knowledge_base_search" 
          ? "Knowledge Base Search" 
          : task.name === "datalake_chat" 
            ? "Security Lake Query" 
            : task.name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
      }));
      
      setAvailableTasks(tasksWithdescriptions);
      
      // Set the default selected task if tasks are available
      if (tasksWithdescriptions.length > 0) {
        // Look for datalake_chat as default or use the first task
        const defaultTask = tasksWithdescriptions.find(task => task.name === "datalake_chat") || tasksWithdescriptions[0];
        setSelectedTask(defaultTask.name);
      }
    } catch (error) {
      console.error("Failed to fetch available tasks:", error);
    } finally {
      setTasksLoading(false);
    }
  };

  const handleSend = async () => {
    if (!userPrompt.trim() || !conversationId || !selectedTask) return;

    // Add user message
    const newMessage: Message = {
      content: userPrompt,
      isUser: true,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, newMessage]);
    setIsLoading(true);

    try {
      // Use the /task/name endpoint with the selected task WITH streaming enabled
      const response = await fetch(`${baseURL}/ac2/api/task/${selectedTask}?stream=true`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-API-Key": "dev-key",
        },
        body: JSON.stringify({
          user_input: userPrompt,
          conversation_id: conversationId,
          scope: alertId || undefined
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Handle streaming response
      if (response.body) {
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let responseText = '';

        // Create an initial assistant message
        setMessages((prev) => [
          ...prev,
          {
            content: '',
            isUser: false,
            timestamp: new Date(),
          },
        ]);

        // Read streaming chunks
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          
          const chunk = decoder.decode(value, { stream: true });
          responseText += chunk;

          // Update the assistant message with accumulated text
          setMessages((prev) => [
            ...prev.slice(0, -1),
            {
              ...prev[prev.length - 1],
              content: responseText,
            },
          ]);
        }
      } else {
        // Fallback for non-streaming response
        const data = await response.json();
        setMessages((prev) => [
          ...prev,
          {
            content: data.response || data.message || "No response received",
            isUser: false,
            timestamp: new Date(),
          },
        ]);
      }
    } catch (error) {
      console.error("Failed to get response:", error);
      setMessages((prev) => [
        ...prev,
        {
          content:
            "Sorry, I encountered an error while processing your request.",
          isUser: false,
          timestamp: new Date(),
        },
      ]);
    } finally {
      setIsLoading(false);
      setUserPrompt("");
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <ChatWrapper>
      {/* Task Selector */}
      <TaskSelector theme={theme} className="pb-4">
        <TaskSelectorTitle theme={theme}><strong>Select a task:</strong></TaskSelectorTitle>
        <TaskOptions>
          {tasksLoading ? (
            <div>Loading available tasks...</div>
          ) : (
            (availableTasks).map((task) => (
              <TaskOption
                key={task.name}
                onClick={() => setSelectedTask(task.name)}
                theme={theme}
                selected={selectedTask === task.name}
              >
                {selectedTask === task.name ? (
                  <RadioButtonCheckedIcon fontSize="small" style={{ marginRight: '4px' }} />
                ) : (
                  <RadioButtonUncheckedIcon fontSize="small" style={{ marginRight: '4px' }} />
                )}
                {task.description || task.name}
              </TaskOption>
            ))
          )}
        </TaskOptions>
      </TaskSelector>

      {alertId && (
        <div style={{ 
          marginBottom: '12px', 
          fontSize: '14px', 
          color: theme.colors.textColor,
          wordBreak: 'break-word', 
          overflowWrap: 'break-word',
          maxWidth: '100%' 
        }}>
          <strong>Using Finding ID:</strong> <p>{alertId}</p>
        </div>
      )}

      {messages.map((message, index) => (
        <div className="message" key={index}>
          <MessageContainer isUser={message.isUser}>
            <Avatar isUser={message.isUser}>
              {message.isUser ? (
                <PersonIcon />
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 49 49"
                  width={80}
                  height={80}
                  fill="#ffffff"
                >
                  <path d="M 33 28 L 31 30 L 30 30 L 30 31 L 31 31 L 32 32 L 32 33 L 33 34 L 33 33 L 35 31 L 36 31 L 36 30 L 35 30 Z" />
                  <path d="M 21 14 L 21 15 L 20 16 L 20 18 L 18 20 L 17 20 L 16 21 L 15 21 L 14 22 L 13 22 L 14 23 L 15 23 L 16 24 L 17 24 L 20 27 L 20 28 L 21 29 L 21 30 L 22 31 L 22 30 L 23 29 L 23 28 L 24 27 L 24 26 L 26 24 L 27 24 L 28 23 L 29 23 L 30 22 L 29 21 L 28 21 L 27 20 L 26 20 L 23 17 L 23 15 L 22 14 Z" />
                </svg>
              )}
            </Avatar>
            <MessageContent isUser={message.isUser}>
              <MessageBubble isUser={message.isUser}>
                <CustomMarkdown content={message.content} />
              </MessageBubble>
              {!message.isUser && (
                <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                  <MessageActions>
                    <IconButton size="small">
                      <ThumbUpIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small">
                      <ThumbDownIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small">
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small">
                      <RefreshIcon fontSize="small" />
                    </IconButton>
                  </MessageActions>
                </div>
              )}
            </MessageContent>
          </MessageContainer>
        </div>
      ))}

      {isLoading && (
        <MessageContainer isUser={false}>
          <Avatar isUser={false}>
            <SmartToyIcon />
          </Avatar>
          <MessageContent>
            <MessageBubble isUser={false}>
              <div className="typing-indicator">Generating response...</div>
            </MessageBubble>
          </MessageContent>
        </MessageContainer>
      )}

      <ChatInputWrapper>
        <Input
          value={userPrompt}
          onChange={(e) => setUserPrompt(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder={`Chat with ${selectedTask || 'Continuous Compliance'}...`}
          disabled={!selectedTask}
        />
        <IconButton
          onClick={handleSend}
          disabled={!userPrompt.trim() || isLoading || !selectedTask}
          style={{
            color: theme.colors.textColor,
            opacity: !userPrompt.trim() || isLoading || !selectedTask ? 0.5 : 1,
          }}
        >
          <SendIcon />
        </IconButton>
      </ChatInputWrapper>
    </ChatWrapper>
  );
};

export default StandaloneChatInterface;